/**
 * Right-sidebar panel describing one extracted Equipment/Instrument (a
 * "subject") — classification, aliases, the documents that reference it
 * (one row per drawing, latest revision), and its linked time-series
 * datastreams. Opened by clicking an alias bounding-box in the viewer.
 * Subject document ids are stable BQ document_ids; entries are resolved
 * under the selected pipeline config's tags via the `data.document_id`
 * field, with an exact-entry-id load alongside for legacy sites whose
 * entry ids ARE the stable ids. A broad tags-only sweep cannot work — a
 * site can carry more system-documents than any load limit, so a capped
 * page misses lookups.
 */
import { Link } from '@tanstack/react-router'
import { Text } from 'designSystem/components/Text'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'designSystem/components/Tooltip'
import { prettify } from 'features/fimEvidence'
import { useDocumentTabDataContext } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import {
  type MatchedDatapoint,
  useTimeSeriesLinkContext,
} from 'features/knowledgeDocumentViewer/contexts/TimeSeriesLinkContext'
import {
  getSystemDocumentBqId,
  type SystemDocumentEntry,
} from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import {
  dedupeSystemDocumentsByDocumentId,
  systemDocumentsByDocumentIds,
} from 'features/knowledgeDocumentViewer/utils/documentEntryLookups'
import { matchSubjectDatapoints } from 'features/knowledgeDocumentViewer/utils/instrumentTagUtils'
import { compareRevisionCodes } from 'features/knowledgeDocumentViewer/utils/revisionHelpers'
import { createTimeSeriesTab } from 'features/knowledgeDocumentViewer/utils/tabStateUrlHelpers'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { ChartLine, ExternalLink, FileText, X } from 'lucide-react'
import { type FC, type ReactNode, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { RolodexEntryType } from 'types'

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  flex-shrink: 0;
  min-height: 0;
  max-height: 50%;
  overflow-y: auto;
  margin: 0.5rem 0.5rem 0.25rem;
  padding: 0.75rem 0.85rem;
  border: 0.0625rem solid ${({ theme }) => theme.forecast.border};
  border-radius: 0.5rem;
  background-color: ${({ theme }) => theme.forecast.cardGroupBackground};
  font-size: 0.83rem;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
`

const HeaderText = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
`

const IdentityFacts = styled.dl`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(5.5rem, 1fr));
  gap: 0.45rem 1rem;
  margin: 0;
`

const IdentityFact = styled.div`
  min-width: 0;
`

const FactLabel = styled.dt`
  margin: 0 0 0.08rem;
  color: #9aa7d8;
  font-size: 0.62rem;
  font-weight: 400;
  letter-spacing: 0.055em;
  text-transform: uppercase;
`

const FactValue = styled.dd`
  margin: 0;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.forecast.activeText};
  font-size: 0.78rem;
  font-weight: 400;
  line-height: 1.25;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`

const SectionTitle = styled.div`
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`

const OpenAllButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
  padding: 0.08rem 0.4rem;
  border-radius: 0.35rem;
  border: 0.0625rem solid ${({ theme }) => theme.forecast.border};
  background: transparent;
  font-size: 0.62rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  color: ${({ theme }) => theme.forecast.secondaryText};

  &:hover {
    border-color: #3b82f6;
    color: #60a5fa;
  }
`

const AliasTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.72rem;
`

const AliasHeadCell = styled.th`
  padding: 0.15rem 0.4rem 0.2rem 0;
  text-align: left;
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.secondaryText};
  border-bottom: 0.0625rem solid ${({ theme }) => theme.forecast.border};
`

const AliasCell = styled.td`
  padding: 0.2rem 0.4rem 0.2rem 0;
  vertical-align: top;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.forecast.activeText};
`

const KindCell = styled.td`
  padding: 0.2rem 0.4rem 0.2rem 0;
  vertical-align: top;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0.15rem;
  border: none;
  background: none;
  cursor: pointer;
  color: ${({ theme }) => theme.forecast.secondaryText};

  &:hover {
    color: ${({ theme }) => theme.forecast.defaultText};
  }
`

const PlainList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`

const SourceItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
  font-size: 0.66rem;
  line-height: 1.35;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

// `font-size: inherit` beats GlobalStyles' layered `div { font-size: 14px }`.
const SourceItemText = styled.div`
  min-width: 0;
  font-size: inherit;
`

const SourceIcon = styled(FileText)`
  flex-shrink: 0;
  margin-top: 0.15rem;
  opacity: 0.7;
`

const TimeSeriesIcon = styled(ChartLine)`
  flex-shrink: 0;
  margin-top: 0.15rem;
  opacity: 0.7;
`

const CurrentDocText = styled.span`
  color: ${({ theme }) => theme.forecast.secondaryText};
  opacity: 0.65;
`

const DocTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.66rem;
  line-height: 1.35;
`

const DocCell = styled.td`
  padding: 0.2rem 0.4rem 0.2rem 0;
  vertical-align: top;
  overflow-wrap: anywhere;
`

// `font-size: inherit` beats GlobalStyles' layered `div { font-size: 14px }`.
const DocCellInner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
  font-size: inherit;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const RevCell = styled.td`
  width: 1%;
  padding: 0.2rem 0 0.2rem 0.3rem;
  vertical-align: top;
  text-align: right;
  white-space: nowrap;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const HoverTip: FC<{ tip: string; children: ReactNode }> = ({
  tip,
  children,
}) => (
  <Tooltip>
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    <TooltipContent>{tip}</TooltipContent>
  </Tooltip>
)

const DocLink = styled.button`
  display: inline;
  padding: 0;
  margin: 0;
  border: none;
  background: none;
  font: inherit;
  text-align: left;
  color: #60a5fa;
  cursor: pointer;
  &:hover {
    color: #93c5fd;
    text-decoration: underline;
  }
`

// Styled via the container: `styled(Link)` erodes the router's type inference.
const HeaderActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;

  a {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.12rem 0.4rem;
    border-radius: 0.35rem;
    border: 0.0625rem solid ${({ theme }) => theme.forecast.border};
    background: transparent;
    font-size: 0.62rem;
    font-weight: 600;
    white-space: nowrap;
    text-decoration: none;
    color: ${({ theme }) => theme.forecast.secondaryText};

    &:hover {
      border-color: #3b82f6;
      color: #60a5fa;
    }
  }
`

/** Must match the process-data tab label the Dossiers evidence panel opens. */
const PROCESS_DATA_LABEL = 'Process Data'

interface SubjectDocumentsPanelProps {
  subjectUid: string
  onClose: () => void
}

/**
 * A document identity (shared `drawing_number`, or the doc id when absent)
 * collapsed to its latest revision.
 */
interface DocRow {
  key: string
  openId: string
  label: string
  revisionCode: string
  isCurrent: boolean
}

interface AliasRow {
  value: string
  kind: string
}

export const SubjectDocumentsPanel: FC<SubjectDocumentsPanelProps> = ({
  subjectUid,
  onClose,
}) => {
  const { addDocumentTab, addTab, openTabs, isGeckoUser } =
    useKnowledgeDocumentUXContext()
  const { documentSubjects, siteID, documentId } = useDocumentTabDataContext()
  const { isPipelineConfigScopeLoading, getPipelineConfigScopedTags } =
    usePipelineConfig()
  const { getMatchedDatapoints } = useTimeSeriesLinkContext()

  const subject = useMemo(
    () => documentSubjects.find((s) => s.subject_uid === subjectUid),
    [documentSubjects, subjectUid]
  )
  const docIds = useMemo(() => subject?.documents ?? [], [subject])

  const aliasRows = useMemo<AliasRow[]>(() => {
    const byKey = new Map<string, AliasRow>()
    for (const alias of subject?.aliases ?? []) {
      const value = alias.value?.trim()
      if (!value) continue
      const key = value.toLowerCase()
      const existing = byKey.get(key)
      if (existing) {
        if (!existing.kind) existing.kind = alias.kind
        continue
      }
      byKey.set(key, { value, kind: alias.kind })
    }
    return [...byKey.values()]
  }, [subject])

  const timeSeriesRows = useMemo<MatchedDatapoint[]>(
    () => matchSubjectDatapoints(subject, getMatchedDatapoints),
    [subject, getMatchedDatapoints]
  )

  const openTimeSeries = useCallback(
    (datapoint: MatchedDatapoint) => {
      const dashboardCount = openTabs.filter((tab) =>
        tab.id.startsWith('timeseries-tab-')
      ).length
      addTab(
        createTimeSeriesTab([datapoint.id], `Dashboard ${dashboardCount + 1}`)
      )
    },
    [addTab, openTabs]
  )

  const openAllTimeSeries = useCallback(() => {
    if (timeSeriesRows.length === 0) return
    addTab(
      createTimeSeriesTab(
        timeSeriesRows.map((datapoint) => datapoint.id),
        PROCESS_DATA_LABEL
      )
    )
  }, [addTab, timeSeriesRows])

  const documentLoadOptions = useMemo(() => {
    return isPipelineConfigScopeLoading || docIds.length === 0
      ? undefined
      : {
          type: [RolodexEntryType.SYSTEM_DOCUMENT],
          ids: docIds,
          tags: getPipelineConfigScopedTags({ site: siteID }),
          limit: docIds.length,
        }
  }, [
    docIds,
    isPipelineConfigScopeLoading,
    getPipelineConfigScopedTags,
    siteID,
  ])

  const { data: sysDocWrappers, isLoading: isLoadingDocs } =
    useGetRolodexEntriesByLoadOptions<SystemDocumentEntry[]>(
      documentLoadOptions
    )

  const stableIdLoadOptions = useMemo(() => {
    return isPipelineConfigScopeLoading || docIds.length === 0
      ? undefined
      : systemDocumentsByDocumentIds(
          docIds,
          getPipelineConfigScopedTags({ site: siteID }),
          docIds.length
        )
  }, [
    docIds,
    isPipelineConfigScopeLoading,
    getPipelineConfigScopedTags,
    siteID,
  ])

  const { data: stableIdDocWrappers, isLoading: isLoadingStableIdDocs } =
    useGetRolodexEntriesByLoadOptions<SystemDocumentEntry[]>(
      stableIdLoadOptions
    )

  const isLoading =
    docIds.length > 0 &&
    (isPipelineConfigScopeLoading || isLoadingDocs || isLoadingStableIdDocs)

  const docRows = useMemo<DocRow[]>(() => {
    const byEntryId = new Map(
      (sysDocWrappers ?? []).map((w) => [w.entry.id, w])
    )
    const byStableId = new Map<string, SystemDocumentEntry>()
    for (const w of dedupeSystemDocumentsByDocumentId(
      stableIdDocWrappers ?? []
    )) {
      const stableId = getSystemDocumentBqId(w)
      if (stableId && !byStableId.has(stableId)) byStableId.set(stableId, w)
    }
    const byKey = new Map<string, DocRow[]>()
    for (const bareId of docIds) {
      const resolved = byStableId.get(bareId) ?? byEntryId.get(bareId)
      const data = resolved?.entry.data
      const openId = resolved?.entry.id ?? bareId
      const label =
        data?.document_title ||
        data?.title ||
        data?.drawing_number ||
        data?.filename ||
        bareId
      const key = data?.drawing_number?.trim() || bareId
      const revision: DocRow = {
        key,
        openId,
        label,
        revisionCode: data?.revision_code ?? '',
        isCurrent: openId === documentId || bareId === documentId,
      }
      const family = byKey.get(key)
      if (family) family.push(revision)
      else byKey.set(key, [revision])
    }
    return [...byKey.values()].map(
      (revisions) =>
        [...revisions].sort(
          (a, b) => -compareRevisionCodes(a.revisionCode, b.revisionCode)
        )[0]
    )
  }, [sysDocWrappers, stableIdDocWrappers, docIds, documentId])

  const showRevColumn = docRows.some((row) => row.revisionCode)

  const timeSeriesFirst =
    subject?.kind === 'instrument' && timeSeriesRows.length > 0

  const timeSeriesSection = timeSeriesRows.length > 0 && (
    <Section>
      <SectionHeader>
        <SectionTitle>Time series</SectionTitle>
        {timeSeriesRows.length > 1 && (
          <HoverTip
            tip={`Open all ${timeSeriesRows.length} datastreams in one Process Data tab`}>
            <OpenAllButton type='button' onClick={openAllTimeSeries}>
              <ChartLine size={10} />
              Open all
            </OpenAllButton>
          </HoverTip>
        )}
      </SectionHeader>
      <PlainList>
        {timeSeriesRows.map((datapoint) => (
          <SourceItem key={datapoint.id}>
            <TimeSeriesIcon size={12} />
            <SourceItemText>
              <HoverTip
                tip={datapoint.description || `Open ${datapoint.pi_tag}`}>
                <DocLink
                  type='button'
                  onClick={() => openTimeSeries(datapoint)}>
                  {datapoint.pi_tag || datapoint.name}
                </DocLink>
              </HoverTip>
            </SourceItemText>
          </SourceItem>
        ))}
      </PlainList>
    </Section>
  )

  return (
    <Wrap>
      <Header>
        <HeaderText>
          <Text
            as='div'
            size='xs'
            color='muted'
            transform='uppercase'
            tracking='wide'>
            Subject
          </Text>
          <Text
            as='div'
            size='sm'
            weight='semibold'
            truncate
            title={subject?.name}>
            {subject?.name ?? 'Subject'}
          </Text>
        </HeaderText>
        <HeaderActions>
          {subject && isGeckoUser && (
            <HoverTip tip='Review this subject in its dossier'>
              <Link
                to='/dossiers'
                search={{
                  siteId: siteID,
                  view: 'review',
                  subjectId: `fim-subject-${subject.subject_uid}`,
                }}
                target='_blank'
                rel='noopener noreferrer'>
                Something wrong?
                <ExternalLink size={11} />
              </Link>
            </HoverTip>
          )}
          <HoverTip tip='Close subject panel'>
            <CloseButton
              type='button'
              onClick={onClose}
              aria-label='Close subject panel'>
              <X size={14} />
            </CloseButton>
          </HoverTip>
        </HeaderActions>
      </Header>

      {subject && (
        <IdentityFacts aria-label='Subject classification'>
          <IdentityFact>
            <FactLabel>Kind</FactLabel>
            <FactValue>{prettify(subject.kind) || 'Not set'}</FactValue>
          </IdentityFact>
          <IdentityFact>
            <FactLabel>Type</FactLabel>
            <FactValue>{prettify(subject.type) || 'Not set'}</FactValue>
          </IdentityFact>
          {subject.review_category && (
            <IdentityFact>
              <FactLabel>Category</FactLabel>
              <FactValue>{prettify(subject.review_category)}</FactValue>
            </IdentityFact>
          )}
        </IdentityFacts>
      )}

      {aliasRows.length > 0 && (
        <Section>
          <AliasTable>
            <thead>
              <tr>
                <AliasHeadCell>Alias</AliasHeadCell>
                <AliasHeadCell>Kind</AliasHeadCell>
              </tr>
            </thead>
            <tbody>
              {aliasRows.map((row) => (
                <tr key={row.value}>
                  <AliasCell>{row.value}</AliasCell>
                  <KindCell>{prettify(row.kind) || '—'}</KindCell>
                </tr>
              ))}
            </tbody>
          </AliasTable>
        </Section>
      )}

      {timeSeriesFirst && timeSeriesSection}

      {isLoading ? (
        <>
          <SectionTitle>Documents</SectionTitle>
          <Text as='span' color='muted'>
            Loading documents…
          </Text>
        </>
      ) : docRows.length === 0 ? (
        <>
          <SectionTitle>Documents</SectionTitle>
          <Text as='span' color='muted'>
            No documents reference this subject.
          </Text>
        </>
      ) : (
        <DocTable>
          <thead>
            <tr>
              <AliasHeadCell>Documents</AliasHeadCell>
              {showRevColumn && <AliasHeadCell>Rev.</AliasHeadCell>}
            </tr>
          </thead>
          <tbody>
            {docRows.map((row) => (
              <tr key={row.key}>
                <DocCell>
                  <DocCellInner>
                    <SourceIcon size={12} />
                    <SourceItemText>
                      {row.isCurrent ? (
                        <HoverTip tip='This document is open in the current tab'>
                          <CurrentDocText>
                            {row.label} (You are here)
                          </CurrentDocText>
                        </HoverTip>
                      ) : (
                        <HoverTip tip={`Open ${row.label}`}>
                          <DocLink
                            type='button'
                            onClick={() =>
                              addDocumentTab({ documentId: row.openId })
                            }>
                            {row.label}
                          </DocLink>
                        </HoverTip>
                      )}
                    </SourceItemText>
                  </DocCellInner>
                </DocCell>
                {showRevColumn && <RevCell>{row.revisionCode || null}</RevCell>}
              </tr>
            ))}
          </tbody>
        </DocTable>
      )}

      {!timeSeriesFirst && timeSeriesSection}
    </Wrap>
  )
}
